// @ts-strict-ignore
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownItem,
  NoStyleButton,
  useToast,
} from "@aviary";
import { DropdownDivider, DropdownLink, OpenAction } from "@aviary/components/Dropdown";
import { usePlatformRedirectMutation } from "@clinic/shared/data/mutations/PlatformRedirectMutation/platformRedirect.mutation";
import { useModularOnboardingModalData } from "@clinic/shared/hooks/useModularOnboardingModalData/useModularOnboardingModalData";
import { StoreSetupSteps } from "@clinic/shared/types/StoreSetupSteps";
import GreenIconSVG from "@emerson/assets/svg/GreenIcon";
import { RsoModal } from "@emerson/components/RsoModal/RsoModal";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { l } from "@emerson/locales/i18n";
import { navigateWithoutRouter } from "@helpers/navigateWithoutRouter/navigateWithoutRouter";
import { ProfileCardAvatar } from "@shared/components/ProfileCardAvatar/ProfileCardAvatar";
import { StopImpersonating } from "@shared/components/StopImpersonating/StopImpersonating";
import { useExperimentVar } from "@shared/context/experiment/experiment.store";
import { useHasMultipleStorePlatformsQuery } from "@shared/data/queries/HasMultipleStorePlatformsQuery/HasMultipleStorePlatforms.query";
import { e2eAttributes } from "@shared/e2eAttributes";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import * as styles from "./AccountDropdown.styles";

const AccountDropdown = () => {
  const { t } = useTranslation();
  const { userId: id } = useExperimentVar();
  const { makeToast } = useToast();

  const { isAdminImpersonating, isUnauthenticated } = useSharedGlobalConfig();

  const { data: officeData } = useHasMultipleStorePlatformsQuery({ skip: isUnauthenticated });
  const { hasMultipleStorePlatforms } = officeData?.currentOffice?.user ?? {};

  const { currentSetupPage, loading } = useModularOnboardingModalData();

  const [isRsoModalOpen, setIsRsoModalOpen] = useState(false);

  const setupComplete = currentSetupPage === StoreSetupSteps.COMPLETE;
  const logAnalyticsEvent = useAnalyticsLogger();

  const handleEnableSharedAccountClick = () => {
    setIsRsoModalOpen(!isRsoModalOpen);
  };

  const logAccountDropdownClick = (isOpen: boolean) => {
    if (isOpen)
      void logAnalyticsEvent({
        eventName: AnalyticsEvent.EMERSON_ACCOUNT_MENU_VIEW_MENU,
        payload: {},
      });
  };
  const renderStopImpersonating = () => {
    if (isAdminImpersonating) {
      return (
        <DropdownItem>
          <StopImpersonating isText />
        </DropdownItem>
      );
    }

    return null;
  };

  const [platformRedirect] = usePlatformRedirectMutation({
    onError: () => {
      makeToast("error", t(l.rso.errorRedirect));
    },
  });

  const exploreFullscript = async () => {
    const { data: redirectData, errors: redirectError } = await platformRedirect({
      variables: {
        input: {
          targetPlatformName: "FULLSCRIPT",
        },
      },
    });

    if (redirectError || redirectData.platformRedirect.errors.message) {
      makeToast("error", t(l.rso.errorRedirect));

      return;
    }

    void logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_ACCOUNT_MENU_SWITCH_PLATFORM,
      payload: {},
    });

    navigateWithoutRouter(redirectData?.platformRedirect?.redirectPath);
  };

  const renderRSOButton = () => {
    if (isUnauthenticated || isAdminImpersonating) {
      return null;
    }

    if (hasMultipleStorePlatforms)
      return (
        <>
          <NoStyleButton
            onClick={exploreFullscript}
            data-testid="rso-bridge-mobile-button"
            css={styles.rsoButton}
          >
            <GreenIconSVG />
            {t(l.header.Account.WritePlans)}
          </NoStyleButton>

          <DropdownDivider />
        </>
      );

    return (
      <>
        <DropdownItem>
          <NoStyleButton
            data-testid="rso-bridge-dropdown-button"
            onClick={handleEnableSharedAccountClick}
            css={styles.rsoButton}
          >
            <GreenIconSVG />
            {t(l.header.Account.WritePlans)}
          </NoStyleButton>
        </DropdownItem>
        <DropdownDivider />
      </>
    );
  };

  return (
    <Dropdown openAction={OpenAction.CLICK} toggleCallback={logAccountDropdownClick} isFullHeight>
      <RsoModal
        hasMultipleStorePlatforms={hasMultipleStorePlatforms}
        isOpen={isRsoModalOpen}
        onClose={() => setIsRsoModalOpen(!isRsoModalOpen)}
      />
      <DropdownButton
        isText
        data-testid="emerson-account-settings"
        data-e2e="emerson_account_settings"
        aria-label="Account Dropdown"
      >
        <ProfileCardAvatar id={id} isSize="xsmall" isBadgeHidden readonly />
      </DropdownButton>
      <DropdownContent css={styles.dropdownContent}>
        {loading || !setupComplete ? (
          <DropdownItem
            disabled
            tabIndex={-1}
            textDirection="left"
            data-e2e="open-account-settings-tab"
          >
            {t(l.header.Account.AccountSettings)}
          </DropdownItem>
        ) : (
          <DropdownLink data-e2e="open-account-settings-tab" to={emersonRoutes.account.basicInfo}>
            {t(l.header.Account.AccountSettings)}
          </DropdownLink>
        )}
        <DropdownLink to={emersonRoutes.orders.orderHistory}>
          {t(l.header.Account.MyOrders)}
        </DropdownLink>
        <DropdownLink href={emersonRoutes.external.support} noExternalIcon>
          {t(l.header.Account.Support)}
        </DropdownLink>
        {renderStopImpersonating()}
        <DropdownDivider />
        {renderRSOButton()}
        <DropdownLink
          href={emersonRoutes.logout}
          target="_self"
          data-e2e={e2eAttributes.practitionerHeader.dropdownLinkSignOut}
        >
          {t(l.header.Logout)}
        </DropdownLink>
      </DropdownContent>
    </Dropdown>
  );
};
export { AccountDropdown };
